import type { CachedCourseData } from "@/lib/contexts/CourseNavigationContext";

export type CourseNavResolverInput = {
    getPrefetchedCourse: (courseId: string) => CachedCourseData | null;
    getCourseListPreview: (courseId: string) => { id?: string | null } | null;
    getCourseIdForLesson: (lessonId: string) => string | null;
    prefetchedCourseData: Record<string, CachedCourseData>;
};

export function hasOutlineModules(c: CachedCourseData | null | undefined): c is CachedCourseData {
    return !!(c?.modules?.length);
}

export function findPrefetchedCourseContainingLesson(
    prefetchedCourseData: Record<string, CachedCourseData>,
    lessonProactiveUuid: string,
): CachedCourseData | null {
    for (const data of Object.values(prefetchedCourseData)) {
        if (!hasOutlineModules(data)) continue;
        for (const mod of data.modules) {
            if ((mod.lessons ?? []).some((l) => l.id === lessonProactiveUuid)) {
                return data;
            }
        }
    }
    return null;
}

export function resolvePrefetchedFullCourse(
    nav: CourseNavResolverInput | null | undefined,
    urlCourseId: string,
    lessonProactiveUuid: string,
): CachedCourseData | null {
    if (!nav) return null;
    const direct = urlCourseId ? nav.getPrefetchedCourse(urlCourseId) ?? null : null;
    if (hasOutlineModules(direct)) return direct;

    const fromLessonIndexCourseId = nav.getCourseIdForLesson(lessonProactiveUuid);
    const fromLessonIndex =
        fromLessonIndexCourseId ? nav.getPrefetchedCourse(fromLessonIndexCourseId) : null;
    if (hasOutlineModules(fromLessonIndex)) return fromLessonIndex;

    const preview = urlCourseId ? nav.getCourseListPreview(urlCourseId) ?? null : null;
    const viaPreviewId =
        preview?.id && preview.id !== urlCourseId ? nav.getPrefetchedCourse(preview.id) ?? null : null;
    if (hasOutlineModules(viaPreviewId)) return viaPreviewId;

    return findPrefetchedCourseContainingLesson(nav.prefetchedCourseData, lessonProactiveUuid);
}
